import React from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import pluralize from 'pluralize';
import * as mime from 'react-native-mime-types';
import { ReactComponent as CloudUpload } from 'assets/images/cloud-upload.svg';
import { Close } from '@mui/icons-material';

const translateErrorMessage = (
  error: { code: string; message: string },
  maxFiles: number,
  fileExtensions: string[],
  fileName: string
) => {
  switch (error.code) {
    case 'too-many-files':
      return `${fileName}\n You can upload a maximum of ${maxFiles} ${pluralize('file', maxFiles)}`;
    case 'file-invalid-type':
      return `${fileName}\n Wrong file type. Supported files: ${fileExtensions
        .map((e) => `.${e}`)
        .join(', ')}`;
    case 'file-too-large':
      return `File too large (${fileName}). Please try uploading another file no larger than 100MB.`;
    default:
      return error.message;
  }
};

interface Props {
  fileExtensions?: string[];
  maxFiles?: number;
  maxSize?: number;
  onFileSelected: (files: File[]) => void;
  disabled?: boolean;
  className?: string;
}

interface Files {
  accepted: File[];
  rejected: FileRejection[];
}

const FileUpload: React.FC<Props> = ({
  fileExtensions = ['csv'],
  maxFiles = 1,
  maxSize,
  onFileSelected = () => {},
  disabled,
  className,
}) => {
  const [files, setFiles] = React.useState<Files>({ accepted: [], rejected: [] });

  // Make sure any errors and primed files are removed upon becoming disabled
  React.useEffect(() => {
    if (disabled) {
      setFiles({ accepted: [], rejected: [] });
    }
  }, [disabled]);

  const onDrop = React.useCallback(
    (accepted: File[], rejected: FileRejection[]) => {
      setFiles({ accepted, rejected });
    },
    [setFiles]
  );

  React.useEffect(() => {
    onFileSelected(files.accepted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files.accepted]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    maxFiles,
    maxSize,
    multiple: maxFiles === 0 || maxFiles > 1,
    onDrop,
    accept: fileExtensions.map((fileType) => mime.lookup(fileType)),
    disabled,
  });

  let fileTypeText = '';
  if (fileExtensions.length > 1) {
    fileTypeText = `All ${fileExtensions
      .map((fileType) => `.${fileType}`)
      .slice(0, -1)
      .join(', ')} and .${fileExtensions.slice(-1)}`;
  } else {
    fileTypeText = `.${fileExtensions[0]}`;
  }

  const acceptedFileItems = files.accepted.map((file) => (
    <li key={file.name} className="text-dark" style={{ lineBreak: 'anywhere' }}>
      {file.name}{' '}
      <Close
        onClick={(e) => {
          e.stopPropagation();
          setFiles((existing) => ({
            ...existing,
            accepted: existing.accepted.filter((f) => f !== file),
          }));
        }}
        style={{ fontSize: 12 }}
      />
    </li>
  ));

  const fileRejectionItems = files.rejected.map(({ file, errors }) => (
    <li key={file.name}>
      <ul>
        {errors.map((e) => (
          <li key={e.code}>
            <h4>{translateErrorMessage(e, maxFiles, fileExtensions, file.name)}</h4>
          </li>
        ))}
      </ul>
    </li>
  ));

  const hasAcceptedFiles = files.accepted.length > 0;

  return (
    <div className="space-y-1">
      <div
        className={`border-dashed border border-${
          hasAcceptedFiles ? 'blue-900' : 'gray-400'
        } p-8 rounded-md w-full flex-grow ${className || ''}`}
        {...getRootProps()}
      >
        <input {...getInputProps()} />
        <div className="w-full h-full flex flex-col items-center justify-items-center space-y-4">
          <CloudUpload
            style={{
              fontSize: 48,
              // blue-900 and gray-500
              // Tailwind won't recolor the svg with className, so we have to do it with style
              stroke: hasAcceptedFiles ? '#0033E8' : '#676C89',
            }}
          />
          <div
            className={`flex flex-col items-center content-center text-gray-${
              disabled ? 600 : 900
            } space-y-1`}
          >
            {isDragActive && <h4>Drop your file here</h4>}
            {!isDragActive &&
              (files.accepted.length === 0 ? (
                <>
                  <h5 className="text-center">
                    Drag and drop or{' '}
                    {disabled ? (
                      'choose a file'
                    ) : (
                      <span className="text-blue-900 underline cursor-pointer">choose a file</span>
                    )}{' '}
                    to upload your containers.
                  </h5>
                  <h5 className="text-center">
                    {fileTypeText} file {pluralize('type', fileExtensions.length)} are supported.{' '}
                    {maxSize && `(Maximum size ${maxSize / (1024 * 1024)} MB)`}
                  </h5>
                </>
              ) : (
                <ul>{acceptedFileItems}</ul>
              ))}
          </div>
        </div>
      </div>
      {fileRejectionItems.length > 0 && <ul className="text-red-900">{fileRejectionItems}</ul>}
    </div>
  );
};

export default FileUpload;
